<template>
  <aside :class="['sidenav', { compact: collapsed }]" aria-label="Main navigation">
    <button type="button" class="sidenav-toggle" :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'" :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'" @click="toggle">
      <i :class="['pi', collapsed ? 'pi-angle-right' : 'pi-angle-left']"></i>
    </button>

    <router-link :to="{ name: DASHBOARD_ROUTE }" class="sidenav-brand" :data-label="orgName">
      <span class="brand-mark">
        <img v-if="orgLogoUrl" :src="orgLogoUrl" :alt="orgName" />
        <span v-else>{{ orgInitials }}</span>
      </span>
      <span class="brand-text">
        <span class="brand-name" :title="orgName">{{ orgName }}</span>
        <span class="brand-sub">{{ isAdmin ? 'Admin workspace' : 'My workspace' }}</span>
      </span>
    </router-link>

    <nav class="sidenav-links">
      <template v-for="group in groups" :key="group.title">
        <div class="sidenav-section">{{ group.title }}</div>
        <div class="sidenav-divider"></div>
        <router-link v-for="item in group.items" :key="item.key" :to="item.to" :class="['sidenav-link', { active: item.active }]" :data-label="item.label">
          <span class="sidenav-icon"><font-awesome-icon :icon="item.icon" /></span>
          <span class="sidenav-label">{{ item.label }}</span>
          <span v-if="item.badge" class="sidenav-badge" :aria-label="`${item.badge} unread`">{{ item.badge > 99 ? '99+' : item.badge }}</span>
        </router-link>
      </template>
    </nav>

    <div class="sidenav-foot">
      <img src="../assets/images/logo.png" alt="abol HR" class="wordmark" />
      <span class="mark" title="abol HR">HR</span>
    </div>

    <!-- Below 992px this rail is hidden (sidenav.css) and the same items show
         in the bottom bar. Teleported so the hidden rail doesn't hide it too. -->
    <teleport to="body">
      <bottom-nav :items="navItems" />
    </teleport>
  </aside>
</template>

<script>
import { DASHBOARD_ROUTE } from '@/router/routes';
import { CLEAR_ERRORMSG } from '@/store/action_types';
import { DEFAULT_ERROR_MSG, STORAGE_URL, ORGANIZATION_LOGO_DIR, TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { isAdminRole } from '@/utils/roles';
import { buildNavGroups } from '@/utils/navigation';
import { sidenavState, toggleSidenav } from '@/utils/sidenav';
import bottomNav from '@/components/Bottom_Nav.vue';

export default {
  name: 'Sidenav',
  components: {
    bottomNav,
  },
  data() {
    return {
      DASHBOARD_ROUTE,
      state: sidenavState,
    };
  },
  computed: {
    errorMessage() {
      return this.$store.getters.errorMsg;
    },
    account() {
      return this.$store.getters.account.data;
    },
    isAdmin() {
      return isAdminRole(this.account?.role);
    },
    collapsed() {
      return this.state.collapsed;
    },
    orgName() {
      return this.account?.organization?.name || 'abol HR';
    },
    orgInitials() {
      const words = String(this.orgName).trim().split(/\s+/).filter(Boolean);
      return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || 'HR';
    },
    orgLogoUrl() {
      const logo = this.account?.organization?.logo;
      return logo ? `${STORAGE_URL}/${ORGANIZATION_LOGO_DIR}/${logo}` : null;
    },
    modules() {
      return this.$store.getters.enabledModules;
    },
    groups() {
      return buildNavGroups({ isAdmin: this.isAdmin, accountId: this.account?.id, routeName: this.$route.name, modules: this.modules, chatUnread: this.$store.getters.chatUnreadTotal });
    },
    navItems() {
      return this.groups.flatMap((group) => group.items);
    },
  },
  watch: {
    errorMessage: {
      handler(val) {
        if (val != null) {
          this.$toast.error(val ?? DEFAULT_ERROR_MSG, TOAST_GLOBAL_CONFIG);
          this.$store.dispatch(CLEAR_ERRORMSG);
        }
      },
    },
  },
  methods: {
    toggle() {
      toggleSidenav();
    },
  },
};
</script>

<style scoped>
.sidenav {
  position: sticky;
  top: 0;
  height: var(--app-height, 100vh);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #E4E4E7;
  padding: 14px 14px 12px 14px;
  min-width: 0;
}

/* ------------------------------ Edge toggle ------------------------------ */
.sidenav-toggle {
  position: absolute;
  top: 26px;
  right: -13px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #E4E4E7;
  background: #ffffff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  z-index: 6;
  padding: 0;
  transition: color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.sidenav-toggle:hover {
  color: #2E7918;
  border-color: #2E7918;
  transform: scale(1.06);
}

.sidenav-toggle .pi {
  font-size: 0.62rem;
}

/* --------------------------------- Brand --------------------------------- */
.sidenav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px 14px 8px;
  min-width: 0;
  text-decoration: none !important;
  color: inherit !important;
  position: relative;
}

.brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #f0f9f0;
  border: 1px solid #E4E4E7;
  color: #2E7918;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.brand-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.brand-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #18181b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.brand-sub {
  font-size: 0.68rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 2px;
}

/* --------------------------------- Links --------------------------------- */
.sidenav-links {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0;
}

.sidenav-section {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9195A1;
  padding: 12px 12px 4px 12px;
  white-space: nowrap;
}

.sidenav-section:first-child {
  padding-top: 2px;
}

.sidenav-divider {
  display: none;
  height: 1px;
  background: #F0F0F2;
  margin: 8px 10px;
}

.sidenav-divider:nth-child(2) {
  display: none !important;
}

.sidenav-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  border-radius: 9px;
  color: #374151 !important;
  font-size: 0.86rem;
  font-weight: 500;
  text-decoration: none !important;
  min-width: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sidenav-link:hover {
  background: #F4F4F5;
  color: #18181b !important;
}

.sidenav-link.active {
  background: #2E7918;
  color: #ffffff !important;
  font-weight: 600;
}

.sidenav-link.active:hover {
  background: #266514;
}

.sidenav-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sidenav-icon .fa,
.sidenav-icon svg {
  font-size: 0.92rem;
  width: 1em;
  height: 1em;
}

.sidenav-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --------------------------------- Footer -------------------------------- */
.sidenav-foot {
  padding: 12px 8px 4px 8px;
  border-top: 1px solid #F0F0F2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidenav-foot .wordmark {
  height: 22px;
  width: auto;
}

.sidenav-foot .mark {
  display: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #2E7918;
  color: #ffffff;
  font-weight: 800;
  font-size: 0.68rem;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.02em;
}

/* -------------------------------- Compact -------------------------------- */
.sidenav.compact {
  padding: 14px 10px 12px 10px;
}

.sidenav.compact .sidenav-brand {
  justify-content: center;
  padding: 4px 0 14px 0;
}

.sidenav.compact .brand-mark {
  width: 42px;
  height: 42px;
  font-size: 0.95rem;
}

.sidenav.compact .brand-text,
.sidenav.compact .sidenav-label,
.sidenav.compact .sidenav-section,
.sidenav.compact .sidenav-foot .wordmark {
  display: none;
}

.sidenav.compact .sidenav-divider {
  display: block;
}

.sidenav.compact .sidenav-foot .mark {
  display: flex;
}

.sidenav.compact .sidenav-link {
  justify-content: center;
  padding: 12px 0;
  gap: 0;
  border-radius: 10px;
}

.sidenav.compact .sidenav-icon {
  width: 26px;
  height: 26px;
}

.sidenav.compact .sidenav-icon .fa,
.sidenav.compact .sidenav-icon svg {
  font-size: 1.15rem;
}

.sidenav.compact .sidenav-foot .mark {
  width: 34px;
  height: 34px;
  font-size: 0.74rem;
}

/* Tooltip with the label when only the icon is visible */
.sidenav.compact .sidenav-link::after,
.sidenav.compact .sidenav-brand::after {
  content: attr(data-label);
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%) translateX(-4px);
  background: #18181b;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 5px 9px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, transform 0.12s ease;
  z-index: 40;
}

.sidenav.compact .sidenav-link:hover::after,
.sidenav.compact .sidenav-brand:hover::after {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

/* Short viewports: the links don't fit the visible height, so scroll them
   and keep the brand and footer pinned in view. The scroller would clip the
   hover tooltips, so those are dropped here. */
@media (max-height: 800px) {
  .sidenav-links {
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .sidenav-links::-webkit-scrollbar {
    display: none;
  }

  .sidenav.compact .sidenav-link::after {
    display: none;
  }
}
</style>
